/**
 * Formate le contenu des notifications pour l'affichage utilisateur.
 * Ne jamais afficher de données sensibles : accountable, sender, service, request_id, etc.
 */

const SENSITIVE_KEYS = ['accountable', 'sender', 'service', 'request_id', 'user_id', 'company_id', 'token', 'id'];

/**
 * Tente de parser une chaîne en JSON (objet de type transaction/callback).
 * @param {string} str
 * @returns {object|null}
 */
function tryParsePayload(str) {
    if (typeof str !== 'string' || !str.trim()) return null;
    const trimmed = str.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) === false) return null;
    try {
        const parsed = JSON.parse(trimmed);
        return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Vérifie si l'objet ressemble à un callback de paiement/transaction.
 * @param {object} obj
 * @returns {boolean}
 */
function isTransactionLike(obj) {
    if (!obj || typeof obj !== 'object') return false;
    const hasAmount = 'amount' in obj && (typeof obj.amount === 'number' || typeof obj.amount === 'string');
    const hasStatus = 'status' in obj && typeof obj.status === 'string';
    return hasAmount || hasStatus;
}

/**
 * Formate un montant pour l'affichage (sans exposer d'autres infos).
 * @param {number|string} amount
 * @param {string} [currency]
 * @returns {string}
 */
function formatAmount(amount, currency = '') {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(num)) return '';
    const formatted = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num);
    return currency ? `${formatted} ${currency}` : formatted;
}

/**
 * Libellé du statut pour l'utilisateur (sans exposer de code technique).
 * @param {string} status
 * @returns {string}
 */
function getStatusLabel(status) {
    if (!status || typeof status !== 'string') return '';
    const s = status.toLowerCase();
    if (s === 'approved' || s === 'accepted') return 'approuvé';
    if (s === 'rejected' || s === 'refused') return 'rejeté';
    if (s === 'pending' || s === 'waiting') return 'en attente';
    if (s === 'cancelled' || s === 'canceled') return 'annulé';
    if (s === 'expired') return 'expiré';
    if (s === 'failed') return 'échoué';
    return status;
}

/**
 * Paiement reçu notifié par le pos-service (demande, lien ou facture).
 * @param {object} payload
 * @returns {boolean}
 */
function isPosPaymentReceived(payload) {
    return Boolean(payload?.target) && typeof payload.message === 'string' && /^paiement de/i.test(payload.message.trim());
}

/**
 * Indique si une notification annonce un paiement reçu.
 * @param {object} notification
 * @returns {boolean}
 */
export function isPaymentReceivedNotification(notification) {
    return formatNotificationTitle(notification) === 'Paiement reçu';
}

/**
 * Retourne un titre sûr pour la notification (à partir du payload si c'est une transaction).
 * @param {object} notification - Objet notification (title, subject, message, body, content, payload, data)
 * @returns {string}
 */
export function formatNotificationTitle(notification) {
    if (!notification) return 'Notification';

    const raw = notification.message ?? notification.body ?? notification.content ?? notification.payload ?? notification.data;
    let payload = typeof raw === 'object' && raw !== null ? raw : tryParsePayload(raw);
    if (!payload && isTransactionLike(notification)) payload = notification;

    // Notification POS « pos-pmt-rcvd » : { target, target_id, message: "Paiement de …" }
    if (payload && isPosPaymentReceived(payload)) return 'Paiement reçu';

    if (payload && isTransactionLike(payload)) {
        const status = getStatusLabel(payload.status);
        if (status === 'approuvé') return 'Paiement reçu';
        if (status === 'rejeté') return 'Paiement rejeté';
        if (status === 'en attente') return 'Paiement en attente';
        if (status === 'annulé') return 'Paiement annulé';
        if (status === 'expiré') return 'Paiement expiré';
        if (status === 'échoué') return 'Paiement échoué';
        if (payload.amount != null) return 'Transaction';
        return 'Notification de paiement';
    }

    const title = notification.title ?? notification.subject;
    if (title && typeof title === 'string' && !looksLikeRawJson(title)) return title;
    return 'Notification';
}

/**
 * Retourne un message sûr pour la notification (jamais de JSON brut ni d'IDs).
 * @param {object} notification
 * @returns {string}
 */
export function formatNotificationMessage(notification) {
    if (!notification) return '';

    const raw = notification.message ?? notification.body ?? notification.content ?? notification.payload ?? notification.data;
    let payload = typeof raw === 'object' && raw !== null ? raw : tryParsePayload(raw);
    if (!payload && isTransactionLike(notification)) payload = notification;

    if (payload && typeof payload.message === 'string' && payload.message.trim() && !looksLikeRawJson(payload.message)) {
        return payload.message;
    }

    if (payload && isTransactionLike(payload)) {
        const parts = [];
        const status = getStatusLabel(payload.status);
        if (status) parts.push(`Statut : ${status}`);
        if (payload.amount != null) {
            parts.push(formatAmount(payload.amount, payload.currency || ''));
        }
        if (payload.currency && payload.amount == null) parts.push(`Devise : ${payload.currency}`);
        return parts.length > 0 ? parts.join(' · ') : 'Nouvelle activité sur votre compte.';
    }

    const msg = notification.message ?? notification.body ?? notification.content;
    if (typeof msg === 'string') {
        if (looksLikeRawJson(msg)) return 'Nouvelle activité sur votre compte.';
        return msg;
    }
    return '';
}

/**
 * Vérifie si la chaîne ressemble à du JSON brut (pour ne pas l'afficher).
 * @param {string} str
 * @returns {boolean}
 */
function looksLikeRawJson(str) {
    if (typeof str !== 'string') return false;
    const t = str.trim();
    if (!t.startsWith('{') || !t.endsWith('}')) return false;
    if (SENSITIVE_KEYS.some((k) => t.includes(`"${k}"`))) return true;
    if (t.includes('"request_id"') || t.includes('"accountable"') || t.includes('"service"')) return true;
    return t.length > 50 && /^\s*\{[\s\S]*\}\s*$/.test(t);
}
